import type { Segment, SegmentData } from '../core';
import { splitIntoSegments } from '../core';
import { SvgPath } from '../svg';
import { none } from './none';

/** The options of {@link Interpolation.monotoneCubic}. */
export interface MonotoneCubicInterpolationOptions {
  /**
   * Joins the line across holes (`null` or `undefined` values) instead of
   * breaking it. Off by default.
   */
  fillHoles?: boolean;
}

/**
 * Splits a segment where x repeats or changes direction. The curve needs
 * strictly monotonic x within a piece, in either direction: a decreasing
 * piece gives the same curve as its points in increasing order.
 */
function splitByDirection(segment: Segment): Segment[] {
  const { pathCoordinates, valueData } = segment;
  const pieces: Segment[] = [];
  let direction = 0;

  for (let i = 0; i < pathCoordinates.length; i += 2) {
    const dx = i >= 2 ? pathCoordinates[i] - pathCoordinates[i - 2] : 0;
    const sign = Math.sign(dx);

    if (i === 0 || sign === 0 || (direction !== 0 && sign !== direction)) {
      pieces.push({ pathCoordinates: [], valueData: [] });
      direction = 0;
    } else {
      direction = sign;
    }

    const piece = pieces[pieces.length - 1];
    piece.pathCoordinates.push(pathCoordinates[i], pathCoordinates[i + 1]);
    piece.valueData.push(valueData[i / 2]);
  }

  return pieces;
}

/**
 * Smooths a line with a monotone cubic spline: a smooth curve through
 * every point that never swings above or below the values around it. It's
 * what `lineSmooth: true` uses.
 *
 * The x values have to keep one direction, increasing or decreasing:
 * where an x value repeats or turns back, the line breaks. With two points
 * or fewer, the line is straight, as with {@link Interpolation.none}.
 *
 * @example
 * ```ts
 * import { lineChart, Interpolation } from 'chartist';
 *
 * lineChart(
 *   '#chart',
 *   { labels: [1, 2, 3, 4, 5], series: [[1, 2, 8, 1, 7]] },
 *   { lineSmooth: Interpolation.monotoneCubic({ fillHoles: true }) }
 * );
 * ```
 *
 * @param options Whether to join the line across holes.
 * @returns The interpolation, for the `lineSmooth` option.
 */
export function monotoneCubic(options?: MonotoneCubicInterpolationOptions) {
  const finalOptions = {
    fillHoles: false,
    ...options
  };

  return function monotoneCubicInterpolation(
    pathCoordinates: number[],
    valueData: SegmentData[]
  ): SvgPath {
    // First we try to split the coordinates into segments
    // This is necessary to treat "holes" in line charts
    const segments = splitIntoSegments(pathCoordinates, valueData, {
      fillHoles: finalOptions.fillHoles
    }).flatMap(splitByDirection);

    if (!segments.length) {
      // If there were no segments return 'Chartist.Interpolation.none'
      return none()([], []);
    } else if (segments.length > 1) {
      // If the split resulted in more that one segment we need to interpolate each segment individually and join them
      // afterwards together into a single path.
      // For each segment we will recurse the monotoneCubic fn function
      // Join the segment path data into a single path and return
      return SvgPath.join(
        segments.map(segment =>
          monotoneCubicInterpolation(segment.pathCoordinates, segment.valueData)
        )
      );
    } else {
      // If there was only one segment we can proceed regularly by using pathCoordinates and valueData from the first
      // segment
      pathCoordinates = segments[0].pathCoordinates;
      valueData = segments[0].valueData;

      // If less than three points we need to fallback to no smoothing
      if (pathCoordinates.length <= 4) {
        return none()(pathCoordinates, valueData);
      }

      const xs = [];
      const ys = [];
      const n = pathCoordinates.length / 2;
      const ms = [];
      const ds = [];
      const dys = [];
      const dxs = [];

      // Populate x and y coordinates into separate arrays, for readability
      for (let i = 0; i < n; i++) {
        xs[i] = pathCoordinates[i * 2];
        ys[i] = pathCoordinates[i * 2 + 1];
      }

      // Calculate deltas and derivative
      for (let i = 0; i < n - 1; i++) {
        dys[i] = ys[i + 1] - ys[i];
        dxs[i] = xs[i + 1] - xs[i];
        ds[i] = dys[i] / dxs[i];
      }

      // Determine desired slope (m) at each point using Fritsch-Carlson method
      // See: http://math.stackexchange.com/questions/45218/implementation-of-monotone-cubic-interpolation
      ms[0] = ds[0];
      ms[n - 1] = ds[n - 2];

      for (let i = 1; i < n - 1; i++) {
        if (ds[i] === 0 || ds[i - 1] === 0 || ds[i - 1] > 0 !== ds[i] > 0) {
          ms[i] = 0;
        } else {
          ms[i] =
            (3 * (dxs[i - 1] + dxs[i])) /
            ((2 * dxs[i] + dxs[i - 1]) / ds[i - 1] +
              (dxs[i] + 2 * dxs[i - 1]) / ds[i]);

          if (!isFinite(ms[i])) {
            ms[i] = 0;
          }
        }
      }

      // Now build a path from the slopes
      const path = new SvgPath().move(xs[0], ys[0], false, valueData[0]);

      for (let i = 0; i < n - 1; i++) {
        path.curve(
          // First control point
          xs[i] + dxs[i] / 3,
          ys[i] + (ms[i] * dxs[i]) / 3,
          // Second control point
          xs[i + 1] - dxs[i] / 3,
          ys[i + 1] - (ms[i + 1] * dxs[i]) / 3,
          // End point
          xs[i + 1],
          ys[i + 1],

          false,
          valueData[i + 1]
        );
      }

      return path;
    }
  };
}
